/*
 * newsbot - Story detail page
 */

import { Suspense } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ClockIcon, TagIcon, ChartBarIcon } from "@heroicons/react/24/outline";

import { storyKeys } from "@api/queries";
import { api } from "@api/client";
import type { Story, Article } from "@/types";
import { formatRelativeTime } from "@utils";
import { getFaviconUrl } from "@utils/favicon";
import { ImportanceBadge } from "@components/stories/ImportanceBadge";

function CategoryBadge({ category }: { category: Story["category"] }) {
  if (!category) {
    return <span className="text-gray-400 dark:text-gray-500 text-sm">Uncategorized</span>;
  }

  return (
    <span className="inline-flex items-center rounded-md bg-purple-50 dark:bg-purple-900/30 px-2.5 py-1 text-sm font-medium text-purple-700 dark:text-purple-300">
      {category.name}
    </span>
  );
}

interface ArticleItemProps {
  article: Article;
}

function ArticleItem({ article }: ArticleItemProps) {
  const domain = (() => {
    try {
      const url = new URL(article.url);
      return url.hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  })();

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-850 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition">
      <div className="flex items-start gap-3">
        {domain && (
          <img
            src={getFaviconUrl(domain)}
            alt=""
            className="w-4 h-4 mt-1 shrink-0"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {domain}
            </span>
            {article.published_at && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatRelativeTime(article.published_at)}
                </span>
              </>
            )}
          </div>
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline line-clamp-2"
          >
            {article.title}
          </a>
        </div>
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function StoryDetail() {
  const { storyId } = useParams({ strict: false });

  if (!storyId || !UUID_RE.test(storyId)) {
    return (
      <main className="max-w-(--breakpoint-xl) mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Story not found</h1>
        <p className="mt-2 text-gray-500 dark:text-gray-400">
          The story ID &ldquo;{storyId || ""}&rdquo; is not valid.
        </p>
        <Link to="/stories" search={{ page: 0, sort: "last_article_at", dir: "desc", q: "", minImportance: undefined, category: "", tags: "", vertical: "" }} className="mt-4 inline-block text-blue-600 dark:text-blue-400 hover:underline">
          &larr; Back to stories
        </Link>
      </main>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      }
    >
      <StoryDetailContent storyId={storyId} />
    </Suspense>
  );
}

function StoryDetailContent({ storyId }: { storyId: string }) {
  const { data: story } = useSuspenseQuery({
    queryKey: storyKeys.detail(storyId),
    queryFn: () => api.get<Story>(`/stories/${storyId}`),
  });

  const { data: articles = [] } = useSuspenseQuery({
    queryKey: storyKeys.articles(storyId),
    queryFn: () => api.get<Article[]>(`/stories/${storyId}/articles`),
  });

  const extraction = story.extractions?.[0];

  return (
    <main>
      {/* Header with breadcrumb */}
      <div className="my-6 max-w-(--breakpoint-xl) mx-auto px-4 sm:px-6 lg:px-8">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Link to="/stories" search={{ page: 0, sort: "last_article_at", dir: "desc", q: "", minImportance: undefined, category: "", tags: "", vertical: "" }} className="hover:text-gray-700 dark:hover:text-gray-200">
            Stories
          </Link>
          <span aria-hidden="true">/</span>
        </nav>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1 truncate" title={story.headline || "Story Details"}>
          {story.headline || "Story Details"}
        </h1>
      </div>

      {/* Main content */}
      <div className="max-w-(--breakpoint-xl) mx-auto pb-12 px-2 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-250 dark:border-gray-775">
          <div className="p-6">

            {/* Metadata */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <TagIcon className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Category:</span>
                <CategoryBadge category={story.category} />
              </div>

              <div className="flex items-center gap-2">
                <ChartBarIcon className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Importance:</span>
                <ImportanceBadge score={story.importance_score} size="md" />
              </div>

              <div className="flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">First seen:</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {formatRelativeTime(story.first_seen_at)}
                </span>
              </div>

              {story.last_article_at && (
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Last article:</span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {formatRelativeTime(story.last_article_at)}
                  </span>
                </div>
              )}
            </div>

            {/* Tags */}
            {story.tags && story.tags.length > 0 && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  {story.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Extraction data */}
            {extraction && (
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                {/* Summary */}
                {extraction.summary && (
                  <Section title="Summary">
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      {extraction.summary}
                    </p>
                  </Section>
                )}

                {/* Suggested titles */}
                {extraction.suggested_titles && extraction.suggested_titles.length > 0 && (
                  <Section title="Suggested Titles">
                    <ul className="space-y-1">
                      {extraction.suggested_titles.map((title, idx) => (
                        <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                          • {title}
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                {/* Key facts */}
                {extraction.key_facts && extraction.key_facts.length > 0 && (
                  <Section title="Key Facts">
                    <ul className="space-y-1.5">
                      {extraction.key_facts.map((fact, idx) => (
                        <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                          • {fact}
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                {/* Notable quotes */}
                {extraction.quotes && extraction.quotes.length > 0 && (
                  <Section title="Notable Quotes">
                    <div className="space-y-3">
                      {extraction.quotes.map((quote, idx) => (
                        <div key={idx} className="pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                          <p className="text-sm italic text-gray-700 dark:text-gray-300">
                            "{quote.text}"
                          </p>
                          {quote.source && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              — {quote.source}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Entities */}
                {extraction.entities && (
                  <Section title="Extracted Entities">
                    {Array.isArray(extraction.entities) ? (
                      <div className="flex flex-wrap gap-2">
                        {extraction.entities.map((entity, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-1 text-xs rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                          >
                            {entity}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {Object.entries(extraction.entities).map(([category, entities]) => (
                          <div key={category}>
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                              {category}:
                            </span>
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {entities.map((entity, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-1 text-xs rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                                >
                                  {entity}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Section>
                )}
              </div>
            )}

            {/* Articles */}
            <Section title={`Articles (${articles.length})`}>
              <div className="space-y-2">
                {articles.length > 0 ? (
                  articles.map((article) => (
                    <ArticleItem key={article.id} article={article} />
                  ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No articles found for this story
                  </p>
                )}
              </div>
            </Section>
          </div>
        </div>
      </div>
    </main>
  );
}
